import React, { useState } from "react";
import { AlertTriangle, ExternalLink, Lock, Shuffle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { envUsing } from "./ContextUsageMap";
import { ContextMovePanel, TYPED_CONTEXTS } from "./ContextMovePanel";
import { ContextMoveProgress } from "./ContextMoveProgress";

function BlockRow({ env, cid }) {
    const v = env.contexts?.[cid] || env;
    const clients = (env.clients || []).filter((c) => c.contexts?.[cid]?.total > 0).slice(0, 6);
    return (
        <li className="rounded border border-amber-400/20 bg-amber-400/5 px-3 py-2" data-testid={`retire-block-${env.env_id}`}>
            <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-white/90">{env.name}{env.is_self && <span className="ml-1.5 text-[10px] text-white/40">(here)</span>}</span>
                <span className="font-mono text-amber-100">{v.total} doc{v.total === 1 ? "" : "s"} · {v.valid} valid</span>
            </div>
            {clients.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {clients.map((c) => <span key={c.client_id} className="rounded-sm border border-white/10 px-1.5 py-0.5 text-[10px] text-white/60">{c.name} · {c.contexts[cid].total}</span>)}
                </div>
            )}
        </li>
    );
}

function MoveButton({ ctx, canMove, onClick }) {
    if (!canMove) return null;
    const typed = TYPED_CONTEXTS.includes(ctx.id);
    return (
        <div className="space-y-1">
            <button onClick={onClick} disabled={typed} className="w-full inline-flex items-center justify-center gap-2 rounded border border-[#7C3AED]/40 bg-[#7C3AED]/10 hover:bg-[#7C3AED]/20 px-3 py-2 text-xs text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid="retire-move-open">
                <Shuffle className="w-3.5 h-3.5" /> Move these documents to another context
            </button>
            {typed && <p className="text-[10px] text-white/45" data-testid="retire-move-typed">Protocols, CTAs, budgets, ICFs and study builds always keep their own context, so this one can't be moved.</p>}
        </div>
    );
}

/** Retire confirmation: blocked (with where) while any environment still has documents with the context — with a one-step move & retire. */
export function RetireContextDialog({ ctx, usage, serverBlock, busy, onClose, onConfirm, contexts, canMove, move, onMoveFinished }) {
    const [mode, setMode] = useState(null); // null · "pick"
    const [started, setStarted] = useState(null);
    if (!ctx) return null;
    const activeMove = started || move;
    const fromMap = envUsing(usage, ctx.id);
    const blocking = serverBlock?.blocking?.length ? serverBlock.blocking.map((b) => fromMap.find((e) => e.env_id === b.env_id || e.name === b.name) || b) : fromMap;
    const unreported = serverBlock?.unreported || usage?.unreported || [];
    const blocked = blocking.length > 0;
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="bg-[#0a0a0a] border border-white/10 text-white max-w-lg [&>*]:min-w-0" data-testid="retire-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-base">
                        {activeMove ? <Shuffle className="w-4 h-4 text-[#A78BFA]" /> : blocked ? <Lock className="w-4 h-4 text-amber-300" /> : <AlertTriangle className="w-4 h-4 text-amber-300" />}
                        {activeMove ? `Move & retire “${ctx.label}”` : blocked ? `Can't retire “${ctx.label}” yet` : `Retire “${ctx.label}”?`}
                    </DialogTitle>
                    <DialogDescription className="text-xs text-white/55">
                        {activeMove
                            ? "Each environment re-tags its own documents; the context retires itself once none are left anywhere. You can close this — the move carries on."
                            : blocked
                            ? `These environments still have documents tagged with this context. ${canMove ? "Move them to another context in one step below, or re-tag" : "Re-tag"} them first (Data Lake → filter by this context → select → Bulk edit), then retire it.`
                            : "No environment has documents with this context. Retiring hides it from pickers and rejects it on new uploads everywhere."}
                    </DialogDescription>
                </DialogHeader>
                {activeMove && <ContextMoveProgress move={activeMove} onFinished={onMoveFinished} />}
                {!activeMove && blocked && <ul className="space-y-2" data-testid="retire-block-list">{blocking.map((e) => <BlockRow key={e.env_id || e.name} env={e} cid={ctx.id} />)}</ul>}
                {!activeMove && blocked && mode !== "pick" && <MoveButton ctx={ctx} canMove={canMove} onClick={() => setMode("pick")} />}
                {!activeMove && blocked && mode === "pick" && (
                    <ContextMovePanel ctx={ctx} contexts={contexts} docs={blocking.reduce((n, e) => n + ((e.contexts?.[ctx.id] || e).total || 0), 0)} envCount={blocking.length}
                        onStarted={(m) => { setStarted(m); if (m.status !== "running") onMoveFinished?.(m); }} onCancel={() => setMode(null)} />
                )}
                {!activeMove && serverBlock?.message && <div className="text-[11px] text-white/50" data-testid="retire-server-message">{serverBlock.message}</div>}
                {!activeMove && unreported.length > 0 && (
                    <div className="text-[11px] text-amber-100/80 border border-amber-400/20 rounded px-3 py-2" data-testid="retire-unreported">
                        Not checked: {unreported.join(", ")} sent no usage report (older build or never connected).
                    </div>
                )}
                <DialogFooter className="gap-2">
                    {blocked && !activeMove && <a href={`/datalake?context=${encodeURIComponent(ctx.id)}`} className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-white mr-auto" data-testid="retire-open-datalake"><ExternalLink className="w-3.5 h-3.5" /> Open in Data Lake</a>}
                    <button onClick={onClose} className="text-xs text-white/60 hover:text-white px-3 py-1.5" data-testid="retire-cancel">{blocked || activeMove ? "Close" : "Cancel"}</button>
                    {!blocked && !activeMove && (
                        <button onClick={onConfirm} disabled={busy} className="text-xs bg-amber-500/90 hover:bg-amber-500 text-black font-medium px-3 py-1.5 rounded transition-colors disabled:opacity-50" data-testid="retire-confirm">
                            {busy ? "Retiring…" : "Retire context"}
                        </button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
